import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewWrapper } from '@tiptap/react'
import {
  AlertCircle,
  Copy,
  Edit2,
  Code,
  Eye,
  EyeOff,
  MoreHorizontal,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'

interface MermaidNodeViewProps {
  node: ProseMirrorNode
  updateAttributes: (attrs: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
  editor: Editor
}

// Lazy load and initialize mermaid once
let mermaidModule: typeof import('mermaid').default | null = null
const initializeMermaid = async () => {
  if (!mermaidModule) {
    const module = await import('mermaid')
    mermaidModule = module.default
    mermaidModule.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'strict',
      themeVariables: {
        primaryColor: '#f3f4f6',
        primaryTextColor: '#1f2937',
        primaryBorderColor: '#e5e7eb',
        lineColor: '#9ca3af',
        secondaryColor: '#fef3c7',
        tertiaryColor: '#ddd6fe',
      },
    })
  }
  return mermaidModule
}

export const MermaidNodeView: React.FC<MermaidNodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  editor,
}) => {
  // Get code from attrs as primary source, content as fallback for backward compatibility
  const codeFromAttrs = node.attrs?.code || ''
  const codeFromContent = node.content?.content?.[0]?.text || ''
  const code = codeFromAttrs || codeFromContent

  const [isEditing, setIsEditing] = useState(false)
  const [showCode, setShowCode] = useState(false)
  const [editedCode, setEditedCode] = useState(code)
  const [isHovered, setIsHovered] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const [error, setError] = useState<string | null>(null)
  const [_copied, setCopied] = useState(false)
  const [_svgContent, setSvgContent] = useState<string>('')
  const containerRef = useRef<HTMLDivElement>(null)
  const [_renderId, setRenderId] = useState(0)

  const renderDiagram = useCallback(async () => {
    if (!containerRef.current || !code.trim()) {
      setSvgContent('')
      return
    }

    try {
      setError(null)

      // Lazy load and initialize mermaid
      const mermaid = await initializeMermaid()

      // Clear any existing content
      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }

      // Generate unique ID for this render
      const id = `mermaid-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

      // Render the diagram
      const { svg } = await mermaid.render(id, code)
      setSvgContent(svg)

      // Set the SVG content
      if (containerRef.current) {
        containerRef.current.innerHTML = svg
      }
    } catch (err) {
      console.error('Mermaid render error:', err)
      setError(err instanceof Error ? err.message : 'Failed to render diagram')
      setSvgContent('')
    }
  }, [code])

  useEffect(() => {
    if (code.trim()) {
      renderDiagram()
    }
  }, [code, renderDiagram])

  // Force re-render when saving
  useEffect(() => {
    if (!isEditing && code.trim()) {
      setRenderId((prev) => prev + 1)
    }
  }, [isEditing, code])

  const handleSave = () => {
    // Avoid dispatching an update when nothing changed to prevent unnecessary transactions
    if (editedCode.trim() === code.trim()) {
      setEditedCode(code)
      setIsEditing(false)
      // Force re-render after canceling
      setTimeout(() => {
        renderDiagram()
      }, 0)
      return
    }

    // Update the node attributes - this is the primary source of truth
    updateAttributes({ code: editedCode })
    setIsEditing(false)
  }

  const handleCancel = () => {
    console.log('code', code)
    setEditedCode(code)
    setIsEditing(false)
    // Force re-render after canceling
    setTimeout(() => {
      renderDiagram()
    }, 0)
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      toast.success('Code copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch (_err) {
      toast.error('Failed to copy code')
    }
  }

  const isReadOnly = !editor.isEditable

  return (
    <NodeViewWrapper
      className={cn(
        'mermaid-node-view group bg-card relative my-4 overflow-hidden rounded-lg border',
        selected && 'ring-primary ring-2 ring-offset-2'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='p-4'>
        {/* Error State */}
        {error && (
          <div className='bg-destructive/10 text-destructive mb-4 flex items-start gap-2 rounded-md p-3 text-sm'>
            <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0' />
            <div>
              <p className='font-medium'>Invalid Mermaid syntax</p>
              <p className='mt-1 text-xs opacity-90'>{error}</p>
            </div>
          </div>
        )}

        {/* Edit Mode */}
        {isEditing ? (
          <div className='space-y-3'>
            <Textarea
              value={editedCode}
              onChange={(e) => setEditedCode(e.target.value)}
              placeholder='Enter Mermaid diagram code...'
              className='min-h-[200px] border-none font-mono text-sm shadow-none focus-visible:ring-0'
              autoFocus
            />
            <div className='flex justify-end gap-2 border-t pt-4'>
              <Button size='sm' variant='outline' className="rounded-md" onClick={handleCancel}>
                Cancel
              </Button>
              <Button size='sm' className="rounded-md bg-sidebar-primary"  onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Code View */}
            {showCode && (
              <div>
                <pre className='overflow-x-auto font-mono text-xs'>
                  <code>{code}</code>
                </pre>
              </div>
            )}

            {/* Diagram */}
            {code.trim() && !error && !isEditing && (
              <div
                ref={containerRef}
                className='mermaid-diagram flex items-center justify-center overflow-x-auto'
              />
            )}

            {/* Empty State */}
            {!code.trim() && !isEditing && (
              <div className='text-muted-foreground py-8 text-center'>
                <Code className='mx-auto mb-2 h-8 w-8 opacity-50' />
                <p className='text-sm'>No diagram code provided</p>
                {!isReadOnly && (
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => {
                      setEditedCode('graph TD\n    A[Start] --> B[End]')
                      setIsEditing(true)
                    }}
                    className='mt-2'
                  >
                    Add Diagram
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Hover Controls */}
      {(isHovered || isPopoverOpen) && !isReadOnly && (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='ghost'
              size='menuAction'
              className='absolute top-2 right-2 z-10'
            >
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            align='end'
            side='bottom'
            sideOffset={8}
            className='!w-fit p-1'
          >
            <div className='space-y-3'>
              {/* <div className='flex items-center justify-between'>
                <span className='text-sm font-medium'>Mermaid Options</span>
              </div> */}

              <div className='flex gap-1'>
                <Button
                  size='xs'
                  variant='ghost'
                  onClick={() => {
                    setShowCode(!showCode)
                    setIsPopoverOpen(false)
                  }}
                  tooltip={showCode ? 'Hide Code' : 'Show Code'}
                >
                  {showCode ? (
                    <>
                      <EyeOff className='h-4 w-4' />
                    </>
                  ) : (
                    <>
                      <Eye className='h-4 w-4' />
                    </>
                  )}
                </Button>

                <Button
                  size='xs'
                  variant='ghost'
                  onClick={() => {
                    handleCopy()
                    setIsPopoverOpen(false)
                  }}
                  tooltip='Copy Code'
                >
                  <Copy className='h-4 w-4' />
                </Button>

                {!isEditing && (
                  <Button
                    size='xs'
                    variant='ghost'
                    onClick={() => {
                      setIsEditing(true)
                      setIsPopoverOpen(false)
                    }}
                    tooltip='Edit Diagram'
                  >
                    <Edit2 className='h-4 w-4' />
                  </Button>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </NodeViewWrapper>
  )
}
